// ============================================================================
// staffInviteService.js - Service layer for staff invite operations
// ============================================================================
// Wraps RPC functions and Edge Function calls for invite management
// ============================================================================

import { supabase } from '../supabaseClient';

/**
 * Create new contractor invite
 * Calls staff_create_invite RPC then triggers email Edge Function
 */
export async function createInvite(inviteData) {
  try {
    // Validate required fields
    if (!inviteData.person_name || !inviteData.person_email) {
      throw new Error('Name and email are required');
    }

    const briefIds = inviteData.brief_ids && inviteData.brief_ids.length > 0
      ? inviteData.brief_ids
      : [null]; // null = auto-select (backward compatible)

    let firstResult = null;

    // Create one submission per selected brief
    for (let i = 0; i < briefIds.length; i++) {
      const { data, error } = await supabase.rpc('staff_create_invite', {
        p_person_name: inviteData.person_name,
        p_person_email: inviteData.person_email,
        p_person_company: inviteData.person_company || null,
        p_person_phone: inviteData.person_phone || null,
        p_person_type: inviteData.person_type || 'contractor',
        p_depot_id: inviteData.depot_id || null,
        p_visit_date: inviteData.visit_date || null,
        p_work_area: inviteData.work_area || null,
        p_nature_of_work: inviteData.nature_of_work || null,
        p_requesting_person: inviteData.requesting_person || null,
        p_expiry_hours: inviteData.expiry_hours || 168, // 7 days default
        p_brief_id: briefIds[i],
        p_force_full_induction: inviteData.forceFullInduction?.[briefIds[i]] || false,
      });

      if (error) throw error;

      // Keep the first result for sending the email (contains token/code)
      if (i === 0) {
        firstResult = data;
      }
    }

    // Send email using the first submission's credentials
    if (inviteData.send_email !== false && firstResult) {
      await sendInviteEmail(firstResult, inviteData.depot_id);
    }

    return {
      success: true,
      reference: firstResult.reference,
      expires_at: firstResult.expires_at,
      email_sent: inviteData.send_email !== false,
      briefs_count: briefIds.length,
    };
  } catch (error) {
    console.error('Create invite error:', error);
    return {
      success: false,
      error: parseRpcError(error),
    };
  }
}

/**
 * Resend existing invite with new token/code
 */
export async function resendInvite(publicReference, expiryHours = 168) {
  try {
    if (!publicReference) {
      throw new Error('Reference code is required');
    }

    // Call RPC to regenerate credentials
    const { data, error } = await supabase.rpc('staff_resend_invite', {
      p_public_reference: publicReference,
      p_expiry_hours: expiryHours,
    });

    if (error) throw error;

    // Send new email
    await sendInviteEmail(data, null); // depot_id already in submission

    return {
      success: true,
      reference: data.reference,
      expires_at: data.expires_at,
    };
  } catch (error) {
    console.error('Resend invite error:', error);
    return {
      success: false,
      error: parseRpcError(error),
    };
  }
}

/**
 * Delete an invite by public reference
 */
export async function deleteInvite(publicReference) {
  try {
    if (!publicReference) {
      throw new Error('Reference code is required');
    }

    const { data, error } = await supabase.rpc('staff_delete_invite', {
      p_public_reference: publicReference,
    });

    if (error) throw error;

    return {
      success: true,
      reference: data.reference,
      person_name: data.person_name,
      pass_revoked: data.pass_revoked === true,
    };
  } catch (error) {
    console.error('Delete invite error:', error);
    return {
      success: false,
      error: parseRpcError(error),
    };
  }
}

/**
 * List invitations with filtering
 */
export async function listInvites(filters = {}) {
  try {
    const { data, error } = await supabase.rpc('staff_list_invites', {
      p_status: filters.status || null,
      p_depot_id: filters.depot_id || null,
      p_limit: filters.limit || 50,
      p_offset: filters.offset || 0,
    });

    if (error) throw error;

    return {
      success: true,
      invites: data || [],
    };
  } catch (error) {
    console.error('List invites error:', error);
    return {
      success: false,
      error: parseRpcError(error),
      invites: [],
    };
  }
}

/**
 * Send invite email via Edge Function
 * @private
 */
async function sendInviteEmail(inviteData, depotId) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    let activeSession = session;

    if (!activeSession) {
      throw new Error('Not authenticated - no active session');
    }

    // Refresh session if token is expired or about to expire
    if (activeSession.expires_at && activeSession.expires_at * 1000 < Date.now() + 60000) {
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !refreshData?.session) {
        throw new Error(refreshError?.message || 'Session refresh failed');
      }

      activeSession = refreshData.session;
    }

    const { data, error } = await supabase.functions.invoke('admin-send-invite-email', {
      body: {
        inviteData,
        depotId,
      },
      headers: {
        Authorization: `Bearer ${activeSession.access_token}`,
      },
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Email sending error:', error);
    // Don't throw - invite created successfully, just email failed
    // Log for staff to manually resend
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Parse RPC error messages to user-friendly format
 */
function parseRpcError(error) {
  if (!error) return 'An unknown error occurred';

  // Extract message from different error formats
  const message = error.message || error.error_description || error.msg || String(error);

  // Map common database errors to user-friendly messages
  const errorMappings = {
    'Person name is required': 'Please enter the person\'s name',
    'Email is required': 'Please enter an email address',
    'Invalid email format': 'Please enter a valid email address',
    'No active induction brief found': 'No active induction brief configured for this person type',
    'Cannot resend invite with status': 'This invite cannot be resent (already submitted or approved)',
    'Invite not found': 'Invite reference not found',
  };

  // Check for known error patterns
  for (const [pattern, friendlyMessage] of Object.entries(errorMappings)) {
    if (message.includes(pattern)) {
      return friendlyMessage;
    }
  }

  // Return original message if no mapping found
  return message;
}

export { parseRpcError };
